import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DentalChart from "../../DentalChart";
import "../../../style/casedetails.css";
import "../../../style/SelectPatient.css";
import "../../../style/patientdetailnew.css";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { ToothData, Patient, OrderDraft, DropdownOption } from "../interface/IPatientDetails";
import { apiAxios } from "../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type PatientDetailsProps = {
  selectedTeeth: ToothData[];
  setSelectedTeeth: React.Dispatch<React.SetStateAction<ToothData[]>>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setOrderDraft: React.Dispatch<React.SetStateAction<OrderDraft | null>>;
  selectedPatient: Patient | null;
  setSelectedPatient: React.Dispatch<React.SetStateAction<Patient | null>>;
};

const PatientDetails: React.FC<PatientDetailsProps> = ({ selectedTeeth, setSelectedTeeth, setActiveStep, setOrderDraft, selectedPatient, setSelectedPatient }) => {

  const [lastClickedTooth, setLastClickedTooth] = useState<number | null>(null);
  const [materials, setMaterials] = useState<DropdownOption[]>([]);
  const [shades, setShades] = useState<DropdownOption[]>([]);
  const [priceSummary, setPriceSummary] = useState({
    singleTotal: 0,
    bridgeTotal: 0,
    grandTotal: 0,
  });

  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  //const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newPatient, setNewPatient] = useState<Omit<Patient, 'patientID'> & { patientID?: number }>({
    patientName: "",
    age: 0,
    gender: "",
    email: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matRes, shadeRes] = await Promise.all([
          apiAxios.get(`${API_BASE}/Material/GetAll`),
          apiAxios.get(`${API_BASE}/Shade/GetAll`),
        ]);
        const matJson = await matRes.data;
        const shadeJson = await shadeRes.data;

        setMaterials(
          (matJson.singleData || []).map((m: any) => ({
            id: m.materialID,
            name: m.materialName,
            price: m.price,
            duration: m.duration,
            unit: m.unit,
          }))
        );

        setShades(
          (shadeJson.singleData || []).map((s: any) => ({
            id: s.shadeID,
            name: s.name,
          }))
        );
      } catch (err) {
        console.error("Failed to load data:", err);
      }
    };
    fetchData();
    fetchPatients();
  }, []);

  // Tooth Click
  const handleToothClick = (toothNumber: number, event?: React.MouseEvent) => {
    setSelectedTeeth((prev) => {
      let updated = [...prev];
      const exists = prev.find((t) => t.toothNumber === toothNumber);

      if (event?.shiftKey && lastClickedTooth) {
        const start = Math.min(lastClickedTooth, toothNumber);
        const end = Math.max(lastClickedTooth, toothNumber);
        const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);

        range.forEach((num) => {
          if (!updated.find((t) => t.toothNumber === num)) {
            updated.push({
              toothNumber: num,
              materialId: null,
              shadeId: null,
              hasBridge: false,
            });
          }
        });
        return updated;
      }

      if (exists) {
        return updated.filter((t) => t.toothNumber !== toothNumber);
      } else {
        updated.push({
          toothNumber,
          materialId: null,
          shadeId: null,
          hasBridge: false,
        });
        return updated;
      }
    });

    setLastClickedTooth(toothNumber);
  };



  // Toggle Bridge
  const toggleBridge = (group: number[]) => {
    setSelectedTeeth((prev) => {
      let updated = [...prev];
      const [a, b] = group;
      const toothA = updated.find((t) => t.toothNumber === a);
      const toothB = updated.find((t) => t.toothNumber === b);
      const bridgeA = toothA?.bridgeGroupId || null;
      const bridgeB = toothB?.bridgeGroupId || null;

      if (bridgeA && bridgeA === bridgeB) {
        const groupTeeth = updated
          .filter((t) => t.bridgeGroupId === bridgeA)
          .map((t) => t.toothNumber)
          .sort((x, y) => x - y);

        const leftGroup = groupTeeth.filter((num) => num < a || num > b);
        const rightGroup = groupTeeth.filter((num) => num > a && num < b);

        updated = updated.map((t) =>
          t.bridgeGroupId === bridgeA
            ? { ...t, hasBridge: false, bridgeGroupId: null }
            : t
        );

        const allBridgeIds = new Set(
          updated.map((t) => t.bridgeGroupId).filter(Boolean)
        );

        const assignNewBridge = (teeth: number[]) => {
          if (!teeth.length) return;
          let newBridgeId = 1;
          while (allBridgeIds.has(newBridgeId)) newBridgeId++;
          allBridgeIds.add(newBridgeId);
          updated = updated.map((t) =>
            teeth.includes(t.toothNumber)
              ? { ...t, hasBridge: true, bridgeGroupId: newBridgeId }
              : t
          );
        };

        assignNewBridge(leftGroup);
        assignNewBridge(rightGroup);

        return updated;
      }

      const teethToBridge = new Set<number>([a, b]);
      if (bridgeA)
        updated
          .filter((t) => t.bridgeGroupId === bridgeA)
          .forEach((t) => teethToBridge.add(t.toothNumber));
      if (bridgeB)
        updated
          .filter((t) => t.bridgeGroupId === bridgeB)
          .forEach((t) => teethToBridge.add(t.toothNumber));

      const allBridgeIds = new Set(
        updated.map((t) => t.bridgeGroupId).filter(Boolean)
      );
      let newBridgeId = 1;
      while (allBridgeIds.has(newBridgeId)) newBridgeId++;

      updated = updated.map((t) =>
        teethToBridge.has(t.toothNumber)
          ? { ...t, hasBridge: true, bridgeGroupId: newBridgeId }
          : t
      );

      return updated;
    });
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const selectedClinic = JSON.parse(sessionStorage.getItem("selectedClinic") || "{}");
      const token = sessionStorage.getItem("token") || "";
      const res = await apiAxios.post(
        `${API_BASE}/Patient/GetAllPatientDashBoard`,
        selectedClinic.clinicID ? { clinicId: selectedClinic.clinicID } : {},
        {
          headers: {
            AuthorizationData: token,
            "Content-Type": "application/json",
          },
        }
      );
      const data = res.data;

      setPatients(data.reverse());
      setFilteredPatients(data.reverse());
    } catch (error) {
      console.error("Error fetching patients:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleSavePatient = async () => {
    if (!newPatient.patientName.trim() || !newPatient.age || !newPatient.gender) {
      toast.success("Please fill in all required fields: Name, Age, and Gender");
      return;
    }
    // ✅ Email format validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newPatient.email && !emailPattern.test(newPatient.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const token = sessionStorage.getItem("token") || "";

      const response = await apiAxios.post(
        `${API_BASE}/Patient/CreatePatient`,
        {
          PatientName: newPatient.patientName,
          Age: newPatient.age,
          Gender: newPatient.gender,
          EmailID: newPatient.email || "",
          ClinicID: JSON.parse(sessionStorage.getItem("selectedClinic") || "{}").clinicID || null
        },
        {
          headers: {
            AuthorizationData: token,
            "Content-Type": "application/json",
          },
        }
      );
      setShowAddForm(false);
      setNewPatient({
        patientName: "",
        age: 0,
        gender: "",
        email: "",
      });
      await fetchPatients();
      const newPatientId = response.data?.patientID;
      if (newPatientId) {
        setSelectedPatient((prev) =>
          prev?.patientID === newPatientId ? prev : null
        );
      }
    } catch (error: any) {
      console.error("Save patient error:", error);
      toast.error(
        error?.response?.data?.message ||
        "Failed to save patient. Please try again."
      );
    }
  };

  const handleCancelAdd = () => {
    setNewPatient({
      patientName: "",
      age: 0,
      gender: "",
      email: "",
    });
    setShowAddForm(false);
  };

  // Add Patient Form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewPatient(prev => ({
      ...prev,
      [name]: name === 'age' ? parseInt(value) || 0 : value
    }));
  };

  // Handle Row Click in DataTable
  const handleRowClick = (row: Patient) => {
    setSelectedPatient(row);
  };


  useEffect(() => {
    const result = patients.filter((p) =>
      (p.patientName ?? "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredPatients(result);
  }, [search, patients]);

  const handleContinue = () => {
    if (!selectedPatient) {
      toast.info("Please select a patient");
      return;
    }

    if (!selectedTeeth.length) {
      toast.info("No teeth selected");
      return;
    }

    const draft: OrderDraft = {
      patientId: selectedPatient.patientID,
      teeth: selectedTeeth,
    };

    setOrderDraft(draft);   // ✅ pass to parent
    setActiveStep(prev => prev + 1); // ✅ go to scans step
  };


  const columns: TableColumn<Patient>[] = [
    {
      name: "ID",
      selector: row => row.patientID,
      width: "90px"
    },
   {
  name: "Name",
  selector: row => row.patientName,
  sortable: true,
  cell: row => (
    <div
      data-tag="allowRowEvents"
      title={row.patientName}
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "200px"
      }}
    >
      {row.patientName}
    </div>
  ),
  style: {
    flexGrow: 2
  }
},
    {
      name: "Age",
      width: "70px",
      cell: row => (
        <div data-tag="allowRowEvents" style={{ width: "100%", textAlign: "center" }}>
          {row.age}
        </div>
      )
    },
    {
      name: "Gender",
      width: "90px",
      cell: row => (
        <div data-tag="allowRowEvents" style={{ width: "100%", textAlign: "center" }}>
          {row.gender}
        </div>
      )
    }
  ];
  
  return (
    <div className="case-details2">
      <div className="tab-content mt-4">
        <div className="tab-pane fade show active">
          <div className="row">
            <div className="col-md-6 col-lg-7 col-12">
              <DentalChart
                selectedTeeth={selectedTeeth}
                handleToothClick={handleToothClick}
                toggleBridge={toggleBridge}
                disabled={true}   // ✅ chart disabled
              />
              <div className="bullet-row">
                <div className="bullet-item">
                  <span className="bullet blue"></span>
                  <span className="bullet-text blue-text">Crown</span>
                </div>

                <div className="bullet-item">
                  <span className="bullet orange"></span>
                  <span className="bullet-text orange-text">Bridge</span>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-5 col-12">

              <>
                {selectedTeeth.length === 0 && (
                  <div className="no-selection-custom">
                    <p className="please-select-teeth">Please select teeth to select a patient.</p>
                  </div>
                )}  

                {selectedTeeth.length > 0 && (
                  <div className="patient-card">
                    <div className="search-container">
                      <ul className="search-listsect">
                        <li>
                          <h5 className="title add-detail select-patient-text">
                            Select Patient
                          </h5>
                        </li>
                        <li>
                          <div className="search-box">
                            <input
                              type="text"
                              placeholder="Search..."
                              className="search-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />
                            <button className="search-btn1">
                              <i className="bi bi-search"></i>
                            </button>
                            <button
                              className="add-btn"
                              onClick={() => {
                                setShowAddForm(true);
                                setSelectedPatient(null);
                              }}
                            >
                              <i className="bi bi-plus-lg"></i>
                            </button>
                          </div>
                        </li>
                      </ul>
                    </div>

                    {!showAddForm && (
                      <DataTable
                        columns={columns}
                        data={filteredPatients}
                        highlightOnHover
                          pointerOnHover
                        progressPending={loading}
                        onRowClicked={handleRowClick}
                        pagination={false}
                        
                        className="patient-table all-selected-tooth"
                        fixedHeader
                        fixedHeaderScrollHeight="330px"
                        conditionalRowStyles={[
                          {
                            when: (row: Patient) =>
                           Number(selectedPatient?.patientID) === Number(row.patientID),
                            style: {
                              backgroundColor: "#e0edf9ff!important",
                              border: "1px solid #3D8BFD!important",
                              borderRadius: "6px",
                              cursor: "pointer",
                            },
                          },
                        ]}
                      />
                    )}

                    {showAddForm && (
                      <div className="add-patient-form">
                        <h4 className="title">Add New Patient</h4>

                        <div className="form-row mb-3">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            name="patientName"
                            className="input-box"
                            placeholder="Enter full name"
                            value={newPatient.patientName}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className="form-row mb-3">
                          <label className="form-label">Age</label>
                          <input
                            type="number"
                            name="age"
                            className="input-box"
                            placeholder="Enter age"
                            value={newPatient.age || ""}
                            onChange={handleInputChange}
                            min="0"
                            max="120"
                          />
                        </div>

                        <div className="form-row mb-3">
                          <label className="form-label">Gender</label>
                          <select
                            name="gender"
                            className="input-box"
                            value={newPatient.gender}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="form-row mb-3">
                          <label className="form-label">Email (Optional)</label>
                          <input
                            type="email"
                            name="email"
                            className="input-box"
                            placeholder="Enter email"
                            value={newPatient.email || ""}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className="form-actions py-3">
                          <button className="save-btn" onClick={handleSavePatient}>
                            Save
                          </button>
                          <button
                            className="cancel-btn btn btn-secondary"
                            onClick={handleCancelAdd}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* <div className="col-md-12 d-flex justify-content-center">
                      <button className="continue-btn2">
                        <div className="clock-icon-image">
                          <img
                            className="clock-icon"
                            src="../flaticon/clock-fill.png"
                          />
                          Latest Delivery in 90 minutes
                        </div>
                      </button>
                    </div> */}

                    <button className="continue-btn" onClick={handleContinue}>
                      Continue
                    </button>
                  </div>
                )}
              </>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetails;